import { HlsVideoElement } from 'hls-video-element';
import { KoiAdsManager } from './ads';
import { AnalyticsConfig, KoiAnalytics } from './analytics';
import BasePlayer from './base-player';
import { CaptionTrackData, KoiConfig, MediaData } from './types';

const posterPlaceholder =
  'data:image/svg+xml;charset=utf-8,%3Csvg xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 viewBox%3D%220 0 1920 1080%22%3E%3Crect width%3D%221920%22 height%3D%221080%22 fill%3D%22%23333%22%2F%3E%3C%2Fsvg%3E';

class KoiVideoPlayer extends BasePlayer {
  #adTagUrl?: string;
  #enableAds: boolean;
  #handleResize?: () => void;
  private readonly creationTimestamp: number;

  constructor() {
    super();
    this.creationTimestamp = Date.now();
    this.#adTagUrl = '';
    this.#enableAds = false;
  }

  private _adsManager?: KoiAdsManager;

  get adsManager(): KoiAdsManager | undefined {
    return this._adsManager;
  }

  private _analytics?: KoiAnalytics;

  get analytics(): KoiAnalytics | undefined {
    return this._analytics;
  }

  get enableAds(): boolean {
    return this.#enableAds;
  }

  get adTagUrl(): string | undefined {
    return this.#adTagUrl;
  }

  _mediaElement!: HTMLMediaElement;

  get mediaElement(): HTMLMediaElement | undefined {
    return this._mediaElement;
  }

  _mediaController!: HTMLElement;

  get mediaController(): HTMLElement | undefined {
    return this._mediaController;
  }

  private initializeAnalytics() {
    if (this._adsManager) {
      throw new Error('KoiPlayer: Analytics should be initialized before ads manager');
    }

    if (!this.orgId || !this.mediaId) {
      throw new Error('KoiPlayer: orgId and mediaId are required for analytics');
    }

    const config: AnalyticsConfig = {
      mediaElement: this._mediaElement!,
      player_init_time: this.creationTimestamp,
      orgId: this.orgId!,
      env: 'sandbox', //todo
      mediaId: this.mediaId!,
      type: 'video',
      meta: this.meta,
      debug: true,
    };
    this._analytics = new KoiAnalytics(config);
    this._analytics.init();
  }

  private initializeAdsManager() {
    if (this.#enableAds && this.#adTagUrl) {
      if (this._mediaElement) {
        this._adsManager = new KoiAdsManager();
        this._adsManager.init(this._mediaElement as HTMLVideoElement, this.#adTagUrl);

        if (this._mediaController) {
          const playButton = this._mediaController.querySelector('media-play-button');
          if (playButton) {
            playButton.addEventListener('click', () => {
              if (this._adsManager && !this._adsManager.isAdPlaying) {
                // TODO: decouple this by using the KoiEventBus
                this._adsManager.playAds();
              }
            });
          }
        }

        this.#handleResize = () => {
          if (this._adsManager) {
            this._adsManager.resize();
          }
        };
        window.addEventListener('resize', this.#handleResize);
      }
    }
  }

  private buildCaptionTracks(captionTracks: CaptionTrackData[]): HTMLElement[] {
    const tracks: HTMLElement[] = [];
    captionTracks.forEach(track => {
      const trackElement = document.createElement('track');
      trackElement.setAttribute('kind', 'captions');
      trackElement.setAttribute('label', track.label);
      trackElement.setAttribute('srclang', track.lang || 'en');
      trackElement.setAttribute('src', track.url);
      tracks.push(trackElement);
    });

    return tracks;
  }

  renderPlayer(videoData: MediaData): void {
    this.hideLoadingState();
    if (this._mediaController) {
      this.unregisterMediaController(this._mediaController);
      this._mediaController.remove();
    }

    this._mediaController = document.createElement('media-controller');
    this.registerMediaController(this._mediaController);

    this._mediaController.setAttribute('defaultsubtitles', '');
    const hlsVideo = document.createElement('hls-video') as HTMLElement;
    hlsVideo.setAttribute('slot', 'media');
    hlsVideo.setAttribute('src', videoData.mediaUrl);
    hlsVideo.setAttribute('poster', videoData.thumbnailUrl || posterPlaceholder);
    hlsVideo.setAttribute('title', videoData.title || this.t('titles.defaultVideo'));
    hlsVideo.setAttribute('crossorigin', '');
    this._mediaController.appendChild(hlsVideo);

    if (videoData.captionTracks && videoData.captionTracks.length > 0) {
      const tracks = this.buildCaptionTracks(videoData.captionTracks);
      for (const track of tracks) {
        hlsVideo.appendChild(track);
      }

      this.meta.captions = true;
    }

    const loadingIndicator = document.createElement('media-loading-indicator');
    loadingIndicator.setAttribute('slot', 'centered-chrome');
    loadingIndicator.setAttribute('noautohide', '');
    this._mediaController.appendChild(loadingIndicator);

    if (videoData.thumbnailUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.referrerPolicy = 'no-referrer-when-downgrade';
      img.onload = () => {
        hlsVideo.setAttribute('poster', videoData.thumbnailUrl!);
      };
      img.onerror = () => {
        console.warn('Failed to load thumbnail image, using placeholder');
      };
      img.src = videoData.thumbnailUrl;
    }

    const controlBar = document.createElement('media-control-bar');

    const controlElements = this.generateControlElements(
      this.type!,
      this.meta.live!,
      this.meta.captions!
    );
    controlBar.appendChild(controlElements);

    this._mediaController.appendChild(controlBar);

    this.shadowRoot.appendChild(this._mediaController);

    if (this._mediaController && typeof this._mediaController.querySelector === 'function') {
      const wrapperElement = this._mediaController.querySelector('hls-video') as HlsVideoElement;
      if (wrapperElement) {
        this._mediaElement = wrapperElement.nativeEl;
      }
    }

    if (!this.stream) {
      this.initializeAnalytics();
    }

    this.initializeAdsManager();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.type = 'video';

    if (this.mediaId && this.stream) {
      console.error('KoiPlayer: Both media-id and stream provided. Only one should be used.');
      this.renderError(this.t('errors.bothMediaAndStream'));
      return;
    }

    if (this.mediaId && !this.orgId) {
      console.error('KoiPlayer: No org-id provided');
      this.renderError(this.t('errors.noOrgId'));
      return;
    }

    if (!this.mediaId && !this.stream) {
      console.error('KoiPlayer: No media-id or stream provided');
      this.renderError(this.t('errors.noMediaOrStream'));
      return;
    }

    if (this.hasAttribute('config')) {
      try {
        const configJson = this.getAttribute('config') || '{}';
        const config = JSON.parse(configJson) as KoiConfig;
        if (config.adTagUrl) this.#adTagUrl = config.adTagUrl;
        if (config.enableAds !== undefined) this.#enableAds = Boolean(config.enableAds);
        if (config.adTagUrl) this.#adTagUrl = config.adTagUrl;
      } catch (error) {
        console.error('KoiPlayer: Error parsing config attribute:', error);
      }
    }

    if (this.mediaId) {
      this.showLoadingState(this.t('loading.video'), { aspectRatio: '16 / 9' });
      this.fetchVideoData(this.mediaId, this.orgId!, this.env)
        .then(videoData => {
          this.meta.ready = true;
          this.renderPlayer(videoData);
          this.finalizeSetup(true);
        })
        .catch(error => {
          console.error('KoiPlayer: Error fetching video data', error);
          this.renderError(this.t('errors.errorLoadingVideo'));
        });
    } else if (this.stream) {
      const videoData: MediaData = {
        mediaUrl: this.stream,
        title: this.getAttribute('title') || undefined,
      };
      this.renderPlayer(videoData);
      this.meta.ready = true;
      this.finalizeSetup(true);
    }
  }

  disconnectedCallback(): void {
    if (this._adsManager) {
      this._adsManager.destroy();
      this._adsManager = undefined;
    }

    if (this.#handleResize) {
      window.removeEventListener('resize', this.#handleResize);
      this.#handleResize = undefined;
    }
  }
}

export default KoiVideoPlayer;
